/**
 * T-0591: what names a built-in view, apart from what defines it.
 *
 * The sidebar lists the built-in views and the page title names the one open, and neither needs the
 * definitions: those are the view engine, about 100 KB minified, which the web entry carried for the
 * sake of two strings. The ids, the names and the descriptions stand here, in a module that imports
 * nothing, and `generic-view-seeds.ts` builds each seed's definition under the heading written here,
 * so a name is written once.
 */
export const GENERIC_VIEW_SEED_IDS = {
  WORK_ITEMS: 'work-items',
  ROADMAP: 'roadmap',
  ACTIVITY: 'activity',
  ACTIVITY_LIST: 'activity-list',
  // F-0205: the Agents view's token usage chart, as a view of its own.
  AGENT_USAGE: 'agent-usage',
} as const;

export interface IGenericViewSeedHeading {
  id: string;
  name: string;
  description: string;
}

/** The built-in views in the order a work area lists them. */
const GENERIC_VIEW_SEED_HEADINGS: readonly IGenericViewSeedHeading[] = [
  {
    id: GENERIC_VIEW_SEED_IDS.WORK_ITEMS,
    name: 'Work items',
    description: 'Work item list and board',
  },
  {
    id: GENERIC_VIEW_SEED_IDS.ROADMAP,
    name: 'Roadmap',
    description: 'Release plan and roadmap board',
  },
  {
    id: GENERIC_VIEW_SEED_IDS.ACTIVITY,
    name: 'Activity',
    description: 'Activity matrix and detail list',
  },
  {
    id: GENERIC_VIEW_SEED_IDS.ACTIVITY_LIST,
    name: 'Activity list',
    description: 'Activity events as an independent list',
  },
  {
    id: GENERIC_VIEW_SEED_IDS.AGENT_USAGE,
    name: 'Token usage',
    description: 'What this work area’s agents spent, in tokens and money, over time',
  },
];

export function listGenericViewSeedHeadings(): IGenericViewSeedHeading[] {
  return GENERIC_VIEW_SEED_HEADINGS.map((heading) => ({ ...heading }));
}

/** The heading of the built-in `id`; throws for an id no seed has, which is a coding error. */
export function genericViewSeedHeading(id: string): IGenericViewSeedHeading {
  const heading = GENERIC_VIEW_SEED_HEADINGS.find((candidate) => candidate.id === id);
  if (!heading) throw new Error(`No built-in view heading for "${id}".`);
  return heading;
}
